import React, { useRef, useMemo, useEffect } from 'react'
import { useFrame, useLoader } from '@react-three/fiber'
import { TextureLoader } from 'three'
import * as THREE from 'three'

export function InstancedForest({ count = 200 }) {
  const meshRef = useRef()
  
  // Load the tree texture
  const texture = useLoader(TextureLoader, '/models/texture-tree-1.0.webp')
  
  // Generate random positions with boundary exclusion
  const matrices = useMemo(() => {
    const matrices = []
    
    // Define boundaries (exclude area around house and path)
    const groundSize = 35 // Total ground area
    const houseExclusionZone = {
      x: [-4, 3],   // Narrower X range (house width)
      z: [-10, 18]    // Extended equally front and back of house
    }
    
    for (let i = 0; i < count; i++) {
      let x, z
      let attempts = 0
      
      // Generate position outside house exclusion zone
      do {
        x = (Math.random() - 0.5) * groundSize
        z = (Math.random() - 0.5) * groundSize
        attempts++
      } while (
        x > houseExclusionZone.x[0] && x < houseExclusionZone.x[1] &&
        z > houseExclusionZone.z[0] && z < houseExclusionZone.z[1] &&
        attempts < 50
      )
      
      const y = 1.5 // Ground level
      
      // Random rotation around Y axis
      const rotation = Math.random() * Math.PI * 2
      
      // Random scale for variety
      const scale = 0.8 + Math.random() * 0.4 // Scale between 0.8 and 1.2
      
      // Create transformation matrix
      const matrix = new THREE.Matrix4()
      matrix.compose(
        new THREE.Vector3(x, y, z),
        new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0.5, 0), rotation),
        new THREE.Vector3(scale, scale, scale)
      )
      
      matrices.push(matrix)
    }
    
    return matrices
  }, [count])
  
  // Set instance matrices when component mounts
  useEffect(() => {
    if (meshRef.current) {
      matrices.forEach((matrix, i) => {
        meshRef.current.setMatrixAt(i, matrix)
      })
      meshRef.current.instanceMatrix.needsUpdate = true
    }
  }, [matrices])
  
  return (
    <instancedMesh ref={meshRef} args={[null, null, count]} castShadow receiveShadow>
      {/* Plane geometry for each tree */}
      <planeGeometry args={[2, 3]} />
      
      {/* Material with tree texture */}
      <meshStandardMaterial
        map={texture}
        transparent
        alphaTest={0.5}
        side={THREE.DoubleSide}
      />
    </instancedMesh>
  )
}